import { useState } from 'react';
import { User, Save, AlertTriangle, CheckCircle2, Lock, Mail, Shield } from 'lucide-react';
import { api } from '../../api/client';
import type { AdminSession } from '../../auth/auth';

export function ProfilePage({ session }: { session: AdminSession }) {
  const [fullName, setFullName] = useState(session.admin.fullName);
  const [email, setEmail] = useState(session.admin.email);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim() || !email.trim()) {
      setProfileError('Ime i email su obavezni');
      return;
    }
    setSavingProfile(true);
    setProfileError(null);
    setProfileSuccess(false);
    try {
      await api.put(`/admin/users/${session.admin.id}`, {
        fullName: fullName.trim(),
        email: email.trim(),
      });
      // Update localStorage session
      const stored = localStorage.getItem('admin-session');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          parsed.admin = { ...parsed.admin, fullName: fullName.trim(), email: email.trim() };
          localStorage.setItem('admin-session', JSON.stringify(parsed));
        } catch { /* ignore */ }
      }
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (e: any) {
      setProfileError(e?.data?.message || e?.message || 'Greška pri čuvanju');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      setPasswordError('Nova lozinka mora imati najmanje 6 karaktera');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Lozinke se ne poklapaju');
      return;
    }
    setSavingPassword(true);
    setPasswordError(null);
    setPasswordSuccess(false);
    try {
      await api.put(`/admin/users/${session.admin.id}`, { password: newPassword });
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(false), 5000);
    } catch (e: any) {
      setPasswordError(e?.data?.message || e?.message || 'Greška pri promeni lozinke');
    } finally {
      setSavingPassword(false);
    }
  };

  const roleLabels: Record<string, string> = {
    admin: 'Admin (puna prava)',
    support: 'Support (čitanje + osnovne izmene)',
    readonly: 'Read-only (samo čitanje)',
  };

  return (
    <div>
      <header style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>Moj profil</h1>
        <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
          Upravljanje vlastitim sysadmin nalogom
        </div>
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, maxWidth: 900 }}>
        {/* Profile info */}
        <div style={panel}>
          <div style={panelHeader}>
            <User size={16} />
            <span>Osnovni podaci</span>
          </div>
          <form onSubmit={handleSaveProfile} style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Field label="Puno ime" icon={<User size={13} />}>
              <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required style={input} />
            </Field>
            <Field label="Email" icon={<Mail size={13} />}>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required style={input} />
            </Field>
            <Field label="Role" icon={<Shield size={13} />}>
              <input type="text" value={roleLabels[session.admin.role] || session.admin.role} disabled style={{ ...input, opacity: 0.6 }} />
            </Field>

            {profileError && <div style={errorBox}><AlertTriangle size={14} /> {profileError}</div>}
            {profileSuccess && <div style={successBox}><CheckCircle2 size={14} /> Profil sačuvan</div>}

            <button type="submit" disabled={savingProfile} style={{ ...btnPrimary, opacity: savingProfile ? 0.5 : 1 }}>
              <Save size={14} /> {savingProfile ? 'Čuvanje...' : 'Sačuvaj izmene'}
            </button>
          </form>
        </div>

        {/* Password change */}
        <div style={panel}>
          <div style={panelHeader}>
            <Lock size={16} />
            <span>Promena lozinke</span>
          </div>
          <form onSubmit={handleChangePassword} style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Field label="Trenutna lozinka">
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="(opciono - server proverava token)"
                style={input}
              />
            </Field>
            <Field label="Nova lozinka *">
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                placeholder="Najmanje 6 karaktera"
                style={input}
              />
            </Field>
            <Field label="Potvrdi novu lozinku *">
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                style={input}
              />
            </Field>

            {passwordError && <div style={errorBox}><AlertTriangle size={14} /> {passwordError}</div>}
            {passwordSuccess && <div style={successBox}><CheckCircle2 size={14} /> Lozinka uspešno promenjena</div>}

            <button type="submit" disabled={savingPassword || !newPassword} style={{ ...btnPrimary, opacity: savingPassword || !newPassword ? 0.5 : 1 }}>
              <Lock size={14} /> {savingPassword ? 'Čuvanje...' : 'Promeni lozinku'}
            </button>
          </form>
        </div>
      </div>

      {/* Session info */}
      <div style={{ ...panel, marginTop: 20, maxWidth: 900 }}>
        <div style={panelHeader}>
          <Shield size={16} />
          <span>Session info</span>
        </div>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12 }}>
          <Row label="Sysadmin ID" value={session.admin.id} mono />
          <Row label="Email" value={session.admin.email} />
          <Row label="Role" value={session.admin.role} />
          <Row label="Token" value={`${session.token.slice(0, 24)}...`} mono />
          <div style={{ marginTop: 8, padding: 10, background: 'var(--color-bg)', borderRadius: 6, fontSize: 11, color: 'var(--color-text-muted)' }}>
            Token važi 12 sati. Posle isteka ćete biti automatski odjavljeni.
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon, children }: { label: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>
        {icon}
        {label}
      </label>
      {children}
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
      <span style={{ color: 'var(--color-text)', fontFamily: mono ? 'monospace' : undefined }}>{value}</span>
    </div>
  );
}

const panel: React.CSSProperties = {
  background: 'var(--color-bg-surface)',
  borderRadius: 10,
  border: '1px solid var(--color-border)',
  overflow: 'hidden',
};

const panelHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '12px 16px',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 13, fontWeight: 600, color: 'var(--color-text-strong)',
};

const input: React.CSSProperties = {
  width: '100%', padding: '9px 12px',
  background: 'var(--color-bg)', color: 'var(--color-text-strong)',
  border: '1px solid var(--color-border)', borderRadius: 6,
  fontSize: 13, outline: 'none', boxSizing: 'border-box',
};

const btnPrimary: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center',
  padding: '10px 16px', borderRadius: 6, border: 'none',
  background: 'var(--color-primary)', color: '#fff',
  fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

const errorBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '10px 12px', borderRadius: 6,
  background: 'rgba(239,68,68,0.15)', color: '#fca5a5',
  fontSize: 13,
};

const successBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '10px 12px', borderRadius: 6,
  background: 'rgba(16,185,129,0.15)', color: '#6ee7b7',
  fontSize: 13,
};
